import type { JSX, RefObject } from 'preact';
import { useState, useEffect, useLayoutEffect, useMemo, useRef, useCallback } from 'preact/hooks';
import { Floating, Offset, Placement, Coords, FlipOptions, ShiftOptions } from '../UNSAFE_Floating';
import {
  FocusableElement,
  focusOn,
  focusWithin,
  getActiveElement
} from '../utils/PRIVATE_tabbableUtils';
import { mergeRefs } from '../utils/PRIVATE_refUtils';
import { FocusTrap } from '../UNSAFE_FocusTrap';
import { Layer } from '../UNSAFE_Layer';
import { Modal } from '../UNSAFE_Modal';
import { LogicalParentRefContext } from '../UNSAFE_Layer/LogicalParentRefContext';
import { WindowOverlay, WindowOverlayPlacement } from '../UNSAFE_WindowOverlay';
import { forwardRef, ForwardedRef } from 'preact/compat';
import type { UnpackSignals } from '@oracle/oraclejet-internal-utilities/attributeUtils';
import { usePopupAnimation } from './usePopupAnimation';
import { AnimationStatus } from '../hooks/PRIVATE_useAnimationStatus';
import { useTestId, TestIdProps } from '../hooks/UNSAFE_useTestId';
import { useOutsideClick } from '../hooks/UNSAFE_useOutsideClick';
import { getClientHints } from '../utils/PRIVATE_clientHints';
import { LiveRegion } from '../UNSAFE_LiveRegion';
import { PopupSkipLink } from './PopupSkipLink';
import { useTranslationBundle } from '../hooks/UNSAFE_useTranslationBundle';
import type { BundleType } from '../resources/nls/bundle';

// Style imports
import { useComponentTheme } from '../hooks/UNSAFE_useComponentTheme';
import { popupVars } from './themes/PopupContract.css';
import { getVarName } from '@oracle/oraclejet-internal-utilities/stringUtils';
import { Property } from 'csstype';
import { classNames } from '../utils/UNSAFE_classNames';
import { PopupRedwoodTheme } from './themes/redwood/PopupTheme';
import { dimensionInterpolations, DimensionProps } from '../utils/UNSAFE_interpolations/dimensions';
import { colorInterpolations, ColorProps } from '../utils/UNSAFE_interpolations/colors';
import { mergeInterpolations } from '../utils/UNSAFE_mergeInterpolations';

import type { UnsafeDomHandle } from '#utils/UNSAFE_unsafeDomAccess';
import { UNSAFE_DOM_ACCESS } from '#utils/UNSAFE_unsafeDomAccess';

type IntrinsicProps = Pick<JSX.HTMLAttributes<HTMLDivElement>, 'children'>;

export type PopupPlacement = Placement | WindowOverlayPlacement;
export type OnCloseDetail = {
  reason: 'escapeKey' | 'closeSkipLink';
};
export type InitialFocus = 'auto' | 'none' | 'popup' | 'firstFocusable';
export type Tail = 'none' | 'simple';
export type Modality = 'modal' | 'modeless';
export type Variant = 'standard' | 'unstyled';

type Props = IntrinsicProps &
  Pick<ColorProps, 'backgroundColor'> &
  DimensionProps &
  TestIdProps & {
    /**
     * Specifies whether the Popup is open.
     */
    isOpen: boolean;
    /**
     * Specifies Popup's anchor. Popup is placed relatively to its anchor.
     */
    anchorRef?: RefObject<Element | Coords | UnsafeDomHandle<Element>>;
    /**
     * Specifies Popup's launcher. After Popup closes, it returns focus to the launcher.
     */
    launcherRef?: RefObject<HTMLElement>;
    /**
     * Specifies placement of the Popup relative to the anchor.
     */
    placement?: PopupPlacement;
    /**
     * Specifies displacement of the Popup from the anchor element along the specified axes.
     * The offset object consists of mainAxis and crossAxis properties. The direction in which these properties
     * are applied depends on the current value of the position property.
     *
     * The <code>mainAxis</code> property represents the distance between the Popup and the anchor.
     * The <code>crossAxis</code> property represents the deviation in the opposite axis to the main axis - the skidding between the Popup and the anchor.
     */
    offset?: Offset;
    /**
     * Specifies modality of the Popup.
     */
    modality?: Modality;
    /**
     * Specifies whether a floating Popup changes placement to the opposite side to be kept in view when a collision is detected.
     *
     * The flipOptions object consists of mainAxis and crossAxis properties.
     * The <code>mainAxis</code> runs along the side of the Popup element.
     * The <code>crossAxis</code> runs along the alignment of the Popup element.
     */
    flipOptions?: FlipOptions;
    /**
     * Specifies whether a floating Popup shifts along axis to be kept in view when a collision is detected.
     * Note that if both 'flip' and 'shift' are enabled on respective axis, the flip will take precedence.
     *
     * The shiftOptions object consists of mainAxis and crossAxis properties.
     * The <code>mainAxis</code> runs along the side of the Popup element.
     * The <code>crossAxis</code> runs along the alignment of the Popup element.
     */
    shiftOptions?: ShiftOptions;
    /**
     * Specifies if the Popup sets focus to its content when initially open.
     * A value of none prevents the popup from setting focus when open.
     *
     * <code>auto</code> in modeless mode resolves to none.
     * <code>none</code> prevents the popup from setting focus when open.
     * <code>popup</code> sets focus to the root popup container (good choice for touch platforms).
     * <code>firstFocusable</code> defines that a popup should set focus to the first focusable element within the popup's content.
     */
    initialFocus?: InitialFocus;
    /**
     * Specifies callback triggered when a user clicks outside Popup.
     */
    onClickOutside?: (event: MouseEvent) => void;
    /**
     * Specifies callback triggered when a user tries to close a Popup through UI interaction.
     * The parent should listen to this event and close the Popup. If the parent fails to remove
     * the Popup, then no change will be done in the UI by the component.
     */
    onClose?: (detail: OnCloseDetail) => void;
    /**
     * Specifies callback triggered when initial focus is set or when Popup receives it via F6 key.
     */
    onFocusSet?: () => void;
    /**
     * Specifies Popup's tail. Simple tail is an arrow pointing to Popup's anchor.
     */
    tail?: Tail;
    /**
     * Specifies callback triggered after the animation ends.
     */
    onTransitionEnd?: (value: boolean) => void;
    /**
     * Specifies styling variant. If <code>unstyled</code> is set, standard styles are not applied.
     */
    variant?: Variant;
    /**
     * Specifies the ARIA role type.
     */
    role?: UnpackSignals<JSX.HTMLAttributes['role']>;
    /**
     * Specifies guidance for screen readers.
     */
    'aria-label'?: string;
    /**
     * Specifies ID of an element used for guidance for screen readers.
     */
    'aria-labelledby'?: string;
    /**
     * Specifies ID of an element (or space separated IDs of multiple elements) that
     * describes the Drawer.
     */
    'aria-describedby'?: string;
  };

/**
 * A popup temporarily 'pops up' content in the foreground.
 */
export const Popup = forwardRef(
  (
    {
      anchorRef,
      launcherRef,
      children,
      isOpen = false,
      modality = 'modeless',
      offset = { mainAxis: 0, crossAxis: 0 },
      flipOptions = { mainAxis: true, crossAxis: true },
      shiftOptions = { mainAxis: false, crossAxis: false },
      onClickOutside,
      onClose,
      initialFocus = 'auto',
      placement = anchorRef ? 'top' : 'center',
      tail = 'none',
      onTransitionEnd,
      onFocusSet,
      variant = 'standard',
      role = 'dialog',
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      'aria-describedby': ariaDescribedBy,
      testId,
      ...props
    }: Props,
    ref: ForwardedRef<HTMLElement | null>
  ) => {
    const localLauncherRef = useRef<HTMLElement | null>(null);
    const [popupBgColor, setPopupBgColor] = useState<Property.BackgroundColor>();

    const contentWrapperRef = useRef<HTMLDivElement | null>(null);

    const prevStatusRef = useRef<AnimationStatus>('unmounted');

    // testId support
    const testIdProps = useTestId(testId);

    const insideRefs: RefObject<HTMLElement>[] = [contentWrapperRef];
    // launcher should be considered an "inside" element
    if (localLauncherRef.current && localLauncherRef.current !== document.body) {
      insideRefs.push(localLauncherRef);
    }

    const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');

    const isModal = useCallback(() => {
      return modality === 'modal';
    }, [modality]);

    // We can not merge outsideClick to onClose event with {reason: 'outsideClick'} payload
    // The reason is that we allow outsideClick/autodismiss in modeless mode and
    // we need to detect outsideClick which is
    useOutsideClick({
      isDisabled: !(isOpen && contentWrapperRef.current),
      ref: insideRefs,
      handler: (event) => {
        onClickOutside?.(event);
      }
    });

    const launcherKeyDownCallback = useCallback(
      (event: KeyboardEvent): void => {
        if (localLauncherRef.current === getActiveElement() && event.code === 'F6' && !isModal()) {
          // Prevent default F6 handlers.
          // F6 is a standard Chrome address bar shortcut on Windows.
          event.preventDefault();
          event.stopPropagation();

          focusWithin(contentWrapperRef.current as HTMLElement);
          onFocusSet?.();
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [isModal]
    );

    const _onTransitionEnd = (isOpen: boolean) => {
      if (isOpen) {
        setInitialFocus();
        onTransitionEnd?.(true);
      } else {
        onTransitionEnd?.(false);
      }
    };

    // Animation
    const { setAnimationElementRef, status, onPosition } = usePopupAnimation({
      isOpen,
      isAnimatedOnMount: true,
      onTransitionEnd: _onTransitionEnd,
      placement: placement
    });

    const stableRef = useMemo(() => mergeRefs(contentWrapperRef, ref), [contentWrapperRef, ref]);

    const returnFocus = useCallback((): void => {
      // Try to return focus to 1.launcher or 2.anchor
      const launcherEl = localLauncherRef.current;
      const anchor = anchorRef?.current;

      if (launcherEl) {
        focusOn(launcherEl);
      } else if (anchor) {
        let anchorElement: FocusableElement | null = null;

        // Checking instanceof to avoid Coords type that can not be focused
        if (anchor instanceof Element) {
          anchorElement = anchor as FocusableElement;
        } else if ((anchor as UnsafeDomHandle)[UNSAFE_DOM_ACCESS]) {
          anchorElement = (anchor as UnsafeDomHandle)[UNSAFE_DOM_ACCESS];
        }

        if (anchorElement) {
          focusOn(anchorElement);
        }
      }
    }, [localLauncherRef, anchorRef]);

    useEffect(() => {
      // Init closing
      if (!isOpen && status === 'opening' && !isModal()) {
        // Only actively return focus to the launcher or anchor if the popup contained focus.
        // Otherwise, focus is already elsewhere on the page and should not be forced back
        // because that could reopen the popup again, which essentially prevents the user from
        // moving focus out of the launcher.
        if (contentWrapperRef.current?.contains(getActiveElement())) {
          returnFocus();
        }
      }
      // JET-71010 - for modal popups, the focus cannot be moved until the modal overlay is gone
      else if (!isOpen && status === 'unmounted' && isModal()) {
        // avoid returning focus on initial render
        if (status !== prevStatusRef.current) {
          returnFocus();
        }
      }
      return () => {
        prevStatusRef.current = status;
      };
    }, [isOpen, status, returnFocus, isModal]);

    // Initialize launcher
    useEffect(() => {
      if (isOpen) {
        if (!launcherRef?.current) {
          // 1. If launcher is not defined, most frequent scenario is,
          // that element, that has focus before opening a popup is considered launcher
          localLauncherRef.current = getActiveElement() as HTMLElement;
        } else if (launcherRef.current instanceof Element) {
          // 2. launcherRef element was provided.
          // Verify, if it exists in DOM, otherwise use the last active element
          localLauncherRef.current = document.body.contains(launcherRef.current)
            ? launcherRef.current
            : (getActiveElement() as HTMLElement);
        } else {
          localLauncherRef.current = getActiveElement() as HTMLElement;
        }
        // Register F6 key handler to enter the Popup
        localLauncherRef.current?.addEventListener('keydown', launcherKeyDownCallback);
      }
      return () => {
        // Deregister F6 key handler
        localLauncherRef.current?.removeEventListener('keydown', launcherKeyDownCallback);
      };
    }, [isOpen, launcherRef, launcherKeyDownCallback]);
    // Note: unlike oj-c-popup we do not default anchorRef to launcherRef in Preact Popup
    // If anchorRef is undefined, we use WindowOverlay as anchor

    const setInitialFocus = () => {
      let derivedInitialFocus = initialFocus;
      if (initialFocus === 'auto') {
        if (isModal()) {
          // modal mode - set focus to first focusable
          derivedInitialFocus = 'firstFocusable';
        } else {
          // modeless mode - do not steal focus by default
          derivedInitialFocus = 'none';
        }
      }

      // Case: 'popup'
      // Focus on Popup container
      if (derivedInitialFocus === 'popup') {
        focusOn(contentWrapperRef.current as HTMLElement);
        onFocusSet?.();
      }

      // Case: 'firstFocusable'
      // Focus on first tabbable in the Popup container or container itself if there is none
      if (derivedInitialFocus === 'firstFocusable') {
        focusWithin(contentWrapperRef.current as HTMLElement);
        onFocusSet?.();
      }
    };

    const getMainAxisOffset = (offset?: Offset) => {
      let calcOffset = 0;
      if (offset) {
        calcOffset = typeof offset === 'number' ? offset : offset.mainAxis || 0;
        if (tail === 'simple') {
          calcOffset += 5;
        }
      }
      return calcOffset;
    };

    const getCrossAxisOffset = (offset?: Offset) => {
      if (!offset) {
        return 0;
      } else {
        return typeof offset === 'number' ? 0 : offset.crossAxis || 0;
      }
    };

    const finalOffset = {
      mainAxis: getMainAxisOffset(offset),
      crossAxis: getCrossAxisOffset(offset)
    };

    const handlePopupKeyDown = (event: KeyboardEvent) => {
      const currentlyFocusedEl = getActiveElement();
      const wrapperEl = contentWrapperRef.current;
      const launcherEl = localLauncherRef.current;
      if (wrapperEl === currentlyFocusedEl || wrapperEl?.contains(currentlyFocusedEl)) {
        switch (event.code) {
          case 'Escape': {
            // Trigger onClose event with outside click detail
            onClose?.({ reason: 'escapeKey' });
            break;
          }
          case 'F6':
            // Prevent default F6 handlers.
            // F6 is a standard Chrome address bar shortcut on Windows.
            event.preventDefault();
            event.stopPropagation();

            // Focus launcher
            if (launcherEl && !isModal()) {
              focusOn(launcherEl);
            }
            break;
        }
      }
    };

    useLayoutEffect(() => {
      if (
        status === 'unmounted' ||
        contentWrapperRef.current == null ||
        popupBgColor != null ||
        tail === 'none'
      )
        return;

      const backgroundColorVar = getVarName(popupVars.backgroundColor);
      const computedStyle = getComputedStyle(contentWrapperRef.current!);
      setPopupBgColor(props.backgroundColor || computedStyle.getPropertyValue(backgroundColorVar));
    }, [status, contentWrapperRef, popupBgColor, tail, props.backgroundColor]);

    const clientHints = getClientHints();
    const isVOSupported = ['ios', 'android'].indexOf(clientHints.platform) > -1;
    // Note: Popup does not have 'open' status. It is 'opening' even when open.
    const renderVOSupport = status === 'opening';

    // Styles
    const { baseTheme, classes } = useComponentTheme(PopupRedwoodTheme);
    const isMounted = status !== 'unmounted';

    const interpolations = [
      ...Object.values(dimensionInterpolations),
      ...Object.values(colorInterpolations)
    ];
    const styleInterpolations = mergeInterpolations<DimensionProps & ColorProps>(interpolations);
    const { ...styles } = styleInterpolations(props);

    const closeSkipLinkCallback = () => {
      // Trigger onClose event with outside click detail
      onClose?.({ reason: 'closeSkipLink' });
    };

    const renderPopup = () => {
      return (
        <>
          <FocusTrap restoreFocusRef={false}>
            {/*Setting restoreFocusRef to false because otherwise FocusTrap is stealing the focus back when :*/}
            {/*we click outside of modeless Popup (that keeps being open).*/}
            <div
              ref={stableRef}
              tabIndex={-1}
              role={role}
              className={classNames([baseTheme, variant === 'unstyled' ? null : classes])}
              style={styles}
              onKeyDown={handlePopupKeyDown}
              aria-label={ariaLabel}
              aria-labelledby={ariaLabelledBy}
              aria-describedby={ariaDescribedBy}
              {...testIdProps}>
              {/* Acc: Close SkipLink */}
              {!isModal() && isVOSupported && renderVOSupport && (
                <PopupSkipLink
                  callback={closeSkipLinkCallback}
                  message={`${translations.popup_close_skipLink()}`}></PopupSkipLink>
              )}
              {children}
            </div>
          </FocusTrap>

          {/* Acc: LiveRegion */}
          {!isModal() &&
            // Render on non-mobile only.
            !isVOSupported && (
              <LiveRegion>
                {`${
                  ['auto', 'none'].indexOf(initialFocus) != -1
                    ? translations.popup_liveRegion_F6_initial_focus_none()
                    : translations.popup_liveRegion_F6_initial_focus_firstFocusable()
                }`}
              </LiveRegion>
            )}
        </>
      );
    };

    const renderFloatingOrOverlayPopup = () => {
      if (!anchorRef || !anchorRef.current) {
        return (
          <WindowOverlay placement={placement as WindowOverlayPlacement} offset={finalOffset}>
            <div ref={setAnimationElementRef}>{renderPopup()}</div>
          </WindowOverlay>
        );
      } else {
        return (
          <Floating
            ref={setAnimationElementRef}
            backgroundColor={popupBgColor}
            anchorRef={anchorRef}
            placement={placement as Placement}
            offsetValue={finalOffset}
            flipOptions={flipOptions}
            shiftOptions={shiftOptions}
            tail={tail}
            onPosition={onPosition}>
            {renderPopup()}
          </Floating>
        );
      }
    };

    if (isMounted) {
      if (isModal()) {
        return (
          <LogicalParentRefContext.Provider value={localLauncherRef}>
            <Modal isOpen={isMounted}>{renderFloatingOrOverlayPopup()}</Modal>
          </LogicalParentRefContext.Provider>
        );
      } else {
        return <Layer logicalParentRef={localLauncherRef}>{renderFloatingOrOverlayPopup()}</Layer>;
      }
    } else return null;
  }
);
